import { getSmartCardsVds } from 'api/getSmartCardsVds';
import { buildAllMetricsBody, buildMetricsFilters, getFilterHash } from 'features/metrics/metricsActions';
import { toSmartCardVdsByScId } from 'features/frontoffice/smartCardVdsOverlay';
import { SMART_CARD_VDS_CACHE_TTL_MS } from 'features/frontoffice/smartCardVdsCache';
import * as Types from './types';

// Dedup concurrent requests for the same tenant + filter context.
const inFlightScVdsRequests = new Map();
const scVdsRequestGeneration = new Map();

// AGENT-CONTEXT: dedup is per cacheKey only, so requests for different tenants /
// filter contexts overlap. A response may only touch the active overlay, tenant,
// filter hash and loading flag while it is still the current context — otherwise a
// slow older response repaints newer values (cross-tenant numbers when hashes
// collide, or a stuck skeleton when they don't). Override only if requests become
// globally cancelled/superseded at dispatch time.
const isCurrentScVdsContext = (getState, tenantCd, filterHash) => {
  const state = getState();
  if (state.user?.currentTenantCd !== tenantCd) return false;
  return getFilterHash(buildMetricsFilters(state.globalFilters?.applied)) === filterHash;
};

/**
 * Fetch /tbl/sc-vds smart-card values for the applied global filters and merge
 * them (by scId) into state.fo.smarts. Fresh values for the same tenant + filter
 * context are served from the localStorage-backed Redux cache without a network call.
 * No-op until a tenant and seeded filters are available.
 */
const SC_VDS_TIMEOUT_MS = 30000;
const SC_VDS_TIMEOUT = 'SC_VDS_TIMEOUT';

const withTimeout = (promise, ms) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(SC_VDS_TIMEOUT)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

export const fetchSmartCardsVds = () => async (dispatch, getState) => {
  const state = getState();
  const tenantCd = state.user?.currentTenantCd;
  const applied = state.globalFilters?.applied;
  const defaultsSeeded = state.globalFilters?.defaultsSeeded;
  if (!tenantCd || !defaultsSeeded) return undefined;

  const body = buildAllMetricsBody(tenantCd, applied);
  const filterHash = getFilterHash(buildMetricsFilters(applied));
  const cacheKey = `${tenantCd}_${filterHash}`;

  // Tenant changed: drop the previous tenant's overlay up front so cards show
  // their own base values (not cross-tenant stale values) while the new tenant's
  // values load — scIds are shared across tenants, so a lingering overlay would
  // otherwise paint the prior tenant's numbers for the whole fetch window.
  const previousTenantCd = state.fo?.smartCardVdsTenantCd;
  if (previousTenantCd && previousTenantCd !== tenantCd) {
    dispatch({
      type: Types.UPDATE_SMART_CARD_VDS_VALUES,
      payload: { byScId: {}, tenantCd }
    });
  }

  // Serve fresh cached values without hitting the (slow) VDS endpoint.
  const cached = state.fo?.smartCardVdsCache?.[cacheKey];
  const isFresh = Boolean(
    cached?.timestamp && (Date.now() - cached.timestamp) < SMART_CARD_VDS_CACHE_TTL_MS
  );
  if (isFresh) {
    dispatch({
      type: Types.UPDATE_SMART_CARD_VDS_VALUES,
      payload: { byScId: cached.byScId, filterHash, cacheKey, timestamp: cached.timestamp, tenantCd }
    });
    return cached.byScId;
  }

  const existing = inFlightScVdsRequests.get(cacheKey);
  if (existing) return existing;

  dispatch({ type: Types.SET_SMART_CARD_VDS_LOADING, payload: { loading: true } });

  const generation = (scVdsRequestGeneration.get(cacheKey) || 0) + 1;
  scVdsRequestGeneration.set(cacheKey, generation);
  const isLatestRequest = () => scVdsRequestGeneration.get(cacheKey) === generation;

const applyVdsResult = (data) => {
  if(!isLatestRequest()) return;
  const byScId = toSmartCardVdsByScId(data);
  dispatch(
    isCurrentScVdsContext(getState, tenantCd, filterHash)
      ? {
          type: Types.UPDATE_SMART_CARD_VDS_VALUES,
          payload: { byScId, filterHash, cacheKey, timestamp: Date.now(), tenantCd }
        }
      : {
          type: Types.CACHE_SMART_CARD_VDS_VALUES,
          payload: { byScId, cacheKey, timestamp: Date.now() }
        }
  );
};

const requestPromise = (async () => {
  const vdsPromise = getSmartCardsVds(body);
  try {
    const data = await withTimeout(vdsPromise, SC_VDS_TIMEOUT_MS);
    applyVdsResult(data);
    return data;
  } catch (error) {
    console.error('Unable to fetch sc-vds smart card values:', error);
    // Settle loading so cards fall back to N/A instead of an endless KPI skeleton.
    if (isCurrentScVdsContext(getState, tenantCd, filterHash)) {
      dispatch({
        type: Types.SET_SMART_CARD_VDS_LOADING,
        payload: { loading: false, filterHash }
      });
    }
    // Timed out but still running: apply the values if they arrive later (AC 8).
    if (error?.message === SC_VDS_TIMEOUT) {
      vdsPromise.then(applyVdsResult).catch(() => {});
      }
    return undefined;
  } finally {
    inFlightScVdsRequests.delete(cacheKey);
  }
})();

  inFlightScVdsRequests.set(cacheKey, requestPromise);
  return requestPromise;
};
